import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TablePagination,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback, useMemo } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import AppLayout from '../components/AppLayout';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../constants/api';
import { formatDate, utcMs } from '../utils/dateFormat';
import type { ExpenseClaimDto } from '../types';
import { useExpenseVatOptions } from '../hooks/useExpenseVatOptions';
import { useExchangeRates } from '../hooks/useExchangeRates';
import { ExchangeRateBanner } from '../components/ExchangeRateBanner';
import AddIcon from '@mui/icons-material/Add';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { getApiErrorMessage } from '../utils/apiError';
import { couldNotLoad } from '../utils/userFacingCopy';

const STATUS_COLOUR: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  Draft: 'default',
  PendingManagerApproval: 'warning',
  Approved: 'success',
  Rejected: 'error',
  Withdrawn: 'info',
  PendingFinanceApproval: 'warning',
};

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'Draft', label: 'Draft' },
  { value: 'PendingManagerApproval', label: 'Pending manager approval' },
  { value: 'Approved', label: 'Approved' },
  { value: 'Rejected', label: 'Rejected' },
  { value: 'Withdrawn', label: 'Withdrawn' },
  { value: 'PendingFinanceApproval', label: 'Pending finance approval' },
];

export default function MyExpensesPage() {
  const { getCurrencySymbol, getCurrencyCode } = useExpenseVatOptions();
  const { rates: exchangeRates, convertToGbp } = useExchangeRates();
  const navigate = useNavigate();
  const [claims, setClaims] = useState<ExpenseClaimDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [sortField, setSortField] = useState<'claimDate' | 'payee' | 'totalGbp' | 'status'>('claimDate');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const filteredClaims = useMemo(() => {
    let result = claims;
    if (statusFilter) {
      result = result.filter((c) => c.status === statusFilter);
    }
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      result = result.filter(
        (c) =>
          c.payee.toLowerCase().includes(q) ||
          formatDate(c.claimDate).toLowerCase().includes(q) ||
          c.total.toFixed(2).includes(q)
      );
    }
    return result;
  }, [claims, searchQuery, statusFilter]);

  const handleSort = (field: 'claimDate' | 'payee' | 'totalGbp' | 'status') => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'claimDate' | 'payee' | 'totalGbp' | 'status') => {
    if (sortField !== field) return <UnfoldMoreIcon sx={{ fontSize: 16, opacity: 0.5 }} />;
    return sortDirection === 'asc'
      ? <ArrowUpwardIcon sx={{ fontSize: 16 }} />
      : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
  };

  const sortedClaims = useMemo(() => {
    return [...filteredClaims].sort((a, b) => {
      let aVal: string | number;
      let bVal: string | number;
      if (sortField === 'claimDate') {
        aVal = utcMs(a.claimDate);
        bVal = utcMs(b.claimDate);
        return sortDirection === 'asc' ? aVal - bVal : bVal - aVal;
      }
      if (sortField === 'payee') {
        aVal = a.payee.toLowerCase();
        bVal = b.payee.toLowerCase();
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }
      if (sortField === 'totalGbp') {
        aVal = convertToGbp(a.total, getCurrencyCode(a.vatRate));
        bVal = convertToGbp(b.total, getCurrencyCode(b.vatRate));
        return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
      }
      aVal = a.status.toLowerCase();
      bVal = b.status.toLowerCase();
      return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredClaims, sortField, sortDirection, convertToGbp, getCurrencyCode]);

  const paginatedClaims = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedClaims.slice(start, start + rowsPerPage);
  }, [sortedClaims, page, rowsPerPage]);

  useEffect(() => {
    setPage(0);
  }, [searchQuery, statusFilter]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await apiService.get<ExpenseClaimDto[]>(API_ENDPOINTS.EXPENSE_CLAIMS.BASE);
      setClaims(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
            setError(getApiErrorMessage(err, couldNotLoad('your expense claims')));
      setClaims([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) {
    return (
      <AppLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
          <CircularProgress />
        </Box>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Box sx={{ maxWidth: '1400px', mx: 'auto' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <ReceiptIcon sx={{ fontSize: 32, color: 'primary.main', alignSelf: 'flex-start', mt: 0.5 }} />
            <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 0 }}>
              Expenses
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => navigate('/expenses/me/new')}
            sx={{ textTransform: 'none' }}
          >
            Submit a new expense claim
          </Button>
        </Box>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            View and manage your expense claims. Submit new claims, track status, and view or edit drafts.
          </Typography>
        </Alert>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}

        <ExchangeRateBanner rates={exchangeRates} />

        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { sm: 'flex-end' } }}>
              <TextField
                fullWidth
                label="Search expense claims"
                placeholder="Search by payee, date, or amount..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                <InputLabel id="expense-status-label" shrink>Status</InputLabel>
                <Select
                  labelId="expense-status-label"
                  label="Status"
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  displayEmpty
                  renderValue={(v) => (v === '' ? 'All statuses' : STATUS_OPTIONS.find((o) => o.value === v)?.label ?? v)}
                >
                  {STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value || 'all'} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            {(searchQuery.trim() !== '' || statusFilter !== '') && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                {searchQuery.trim() !== '' && (
                  <Chip
                    label={`Search: "${searchQuery}"`}
                    onDelete={() => setSearchQuery('')}
                    size="small"
                    color="primary"
                  />
                )}
                {statusFilter !== '' && (
                  <Chip
                    label={`Status: ${STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ?? statusFilter}`}
                    onDelete={() => setStatusFilter('')}
                    size="small"
                    color="primary"
                  />
                )}
              </Box>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            {filteredClaims.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 6 }}>
                <ReceiptIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  {(searchQuery || statusFilter) ? 'No expense claims match your search or filters.' : 'You have no expense claims yet'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {(searchQuery || statusFilter)
                    ? 'Try adjusting your search or status filter.'
                    : 'Use "Submit a new expense claim" to create one.'}
                </Typography>
              </Box>
            ) : (
              <>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleSort('claimDate')}
                          >
                            <strong>Claim date</strong>
                            {getSortIcon('claimDate')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleSort('payee')}
                          >
                            <strong>Payee</strong>
                            {getSortIcon('payee')}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Total</strong>
                        </TableCell>
                        <TableCell align="right">
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              justifyContent: 'flex-end',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleSort('totalGbp')}
                          >
                            <strong>Total (GBP)</strong>
                            {getSortIcon('totalGbp')}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              cursor: 'pointer',
                              userSelect: 'none',
                              '&:hover': { opacity: 0.7 },
                            }}
                            onClick={() => handleSort('status')}
                          >
                            <strong>Status</strong>
                            {getSortIcon('status')}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <strong>Actions</strong>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {paginatedClaims.map((claim) => (
                        <TableRow key={claim.id} hover>
                          <TableCell>{formatDate(claim.claimDate)}</TableCell>
                          <TableCell>{claim.payee}</TableCell>
                          <TableCell align="right">{getCurrencySymbol(claim.vatRate)}{claim.total.toFixed(2)}</TableCell>
                          <TableCell align="right">£{convertToGbp(claim.total, getCurrencyCode(claim.vatRate)).toFixed(2)}</TableCell>
                          <TableCell>
                            <Chip
                              label={claim.status}
                              size="small"
                              color={STATUS_COLOUR[claim.status] ?? 'default'}
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Button
                              size="small"
                              endIcon={<OpenInNewIcon />}
                              onClick={() => navigate(`/expenses/me/new?edit=${claim.id}`)}
                              sx={{ textTransform: 'none' }}
                            >
                              {claim.status === 'Draft' ? 'Edit' : 'View'}
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
                <TablePagination
                  component="div"
                  count={sortedClaims.length}
                  page={page}
                  onPageChange={(_, newPage) => setPage(newPage)}
                  rowsPerPage={rowsPerPage}
                  onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  sx={{ borderTop: '1px solid', borderColor: 'divider', flexShrink: 0 }}
                />
              </>
            )}
          </CardContent>
        </Card>
      </Box>
    </AppLayout>
  );
}
